import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import FormControl from '@mui/material/FormControl';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import Checkbox from '@mui/material/Checkbox';
import Tooltip, { TooltipProps } from '@mui/material/Tooltip';
import type { PopperProps } from '@mui/material/Popper';
import { ReactComponent as ArrowDown } from '~/shared/assets/arrowDown.svg';

export interface MultiSelectDropdownOption {
  id?: string;
  value?: string;
  iso?: string;
  name?: string;
  label?: string;
  description?: string;
  [key: string]: unknown;
}

export interface MultiSelectDropdownTooltipConfig {
  color?: string;
  backgroundColor?: string;
  opacity?: number;
  borderColor?: string;
  borderStyle?: string;
  borderWidth?: string | number;
  borderRadius?: string;
  padding?: string;
  maxWidth?: string;
  headerFontSize?: string;
  headerFontWeight?: number | string;
  headerMarginBottom?: string;
  contentFontSize?: string;
  contentFontWeight?: number | string;
  letterSpacing?: number;
}

export interface MultiSelectDropdownTooltip {
  header?: React.ReactNode;
  content?: React.ReactNode;
  config?: MultiSelectDropdownTooltipConfig;
}

export interface MultiSelectDropdownProps<TOption extends MultiSelectDropdownOption = MultiSelectDropdownOption> {
  label: string;
  options: TOption[];
  value: string | string[] | null | undefined;
  onChange: (value: string | string[]) => void;
  multiple?: boolean;
  disabled?: boolean;
  placeholder?: string;
  tooltip?: MultiSelectDropdownTooltip;
  optionTooltip?: (option: TOption) => MultiSelectDropdownTooltip | undefined;
  menuOffset?: { horizontal: number; vertical: number };
  getOptionLabel?: (option: TOption) => string;
  getOptionValue?: (option: TOption) => string;
  summaryFormatter?: (selectedOptions: TOption[]) => { label: string; title?: string };
  popperModifiers?: PopperProps['modifiers'];
  className?: string;
}

const DEFAULT_MENU_OFFSET = { horizontal: 4, vertical: -2 };

const SelectContainer = styled(FormControl)`
  min-width: 167px;
  border-radius: 4px 4px 0 0;
  margin-top: 0px !important;
  .MuiAutocomplete-root {
    border: none;
  }
`;

const StyledAutocomplete = styled(Autocomplete)<{ $isopen: number }>`
  border-radius: 5px;
  background: white !important;
  margin-top: 13px;
  margin-right: 12px;
  height: 40px;
  font-size: 13px;
  color: #4c5e72;
  font-weight: 400;
  max-width: 154px;
  width: 154px;
  & .MuiAutocomplete-inputRoot {
    padding: 0 !important;
    overflow: hidden;
  }
  & .MuiOutlinedInput-root {
    padding: 0 !important;
    height: 40px;
    min-height: 40px;
    background: transparent;
    border-radius: 5px;
    border: 1px solid ${({ $isopen }) => ($isopen ? 'var(--filter-bar-dropdown-border-active, #33485e)' : '#99a3af')};
    border-bottom: ${({ $isopen }) => ($isopen ? 'none' : undefined)};
    border-bottom-left-radius: ${({ $isopen }) => ($isopen ? 0 : '5px')};
    border-bottom-right-radius: ${({ $isopen }) => ($isopen ? 0 : '5px')};
  }
  & .MuiOutlinedInput-root:hover {
    border-color: var(--filter-bar-dropdown-border-active, #33485e);
  }
  & .MuiOutlinedInput-notchedOutline {
    display: none;
  }
  & .MuiOutlinedInput-root.Mui-focused {
    border: 1px solid ${({ $isopen }) => ($isopen ? 'var(--filter-bar-dropdown-border-active, #33485e)' : '#99a3af')};
    box-shadow: none;
  }
  & .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline {
    display: none;
  }
  & .MuiOutlinedInput-root .MuiAutocomplete-input {
    padding: 5px 4px !important;
    font-size: 15px !important;
    font-weight: 400 !important;
    color: #33485e !important;
  }
  .MuiInputLabel-animated {
    font-size: 13px;
    transform: translate(-4px, -16px);
    opacity: 0;
  }
  .MuiInputLabel-shrink {
    opacity: 1;
    font-size: 13px;
    transform: translate(-4px, -16px);
    padding: 0 4px 3px;
    color: #4c5e72 !important;
    background: #f5f5f5;
    line-height: 1em;
  }
  label + .MuiInput-formControl {
    margin-left: 12px;
    width: calc(100% - 16px);
  }
  .MuiIconButton-root {
    max-width: 20px !important;
    max-height: 20px !important;
  }
  .MuiAutocomplete-popupIndicator {
    padding: 8px 2px 2px !important;
  }
  .MuiAutocomplete-endAdornment {
    top: calc(50% - 3px) !important;
    right: 9px !important;
  }
  .MuiAutocomplete-option {
    padding: 6px 16px !important;
  }
  .MuiAutocomplete-input:first-child {
    color: #33485e !important;
    padding: 5px 4px !important;
    font-size: 15px !important;
    font-weight: 400 !important;
    margin-left: 12px !important;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: calc(100% - 36px) !important;
    width: calc(100% - 36px) !important;
  }
  border: 1px solid ${({ $isopen }) => ($isopen ? 'var(--filter-bar-dropdown-border-active, #33485e)' : '#99a3af')};
  border-bottom: ${({ $isopen }) => ($isopen ? 'none' : undefined)};
  border-bottom-left-radius: ${({ $isopen }) => ($isopen ? 0 : '5px')};
  border-bottom-right-radius: ${({ $isopen }) => ($isopen ? 0 : '5px')};
  & .MuiAutocomplete-tag {
    background: transparent;
    border: none;
    margin: 0;
    padding: 0;
    max-width: calc(100% - 36px);
  }
  & .MuiAutocomplete-tag > span {
    display: block;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    font-size: 15px;
    font-weight: 400;
    color: #33485e;
    padding: 5px 4px !important;
    margin-left: 12px;
  }
`;

const StyledTooltipHeader = styled.div<MultiSelectDropdownTooltipConfig>`
  font-weight: ${({ headerFontWeight }) => headerFontWeight ?? 500};
  font-size: ${({ headerFontSize }) => headerFontSize ?? '15px'};
  margin-bottom: ${({ headerMarginBottom }) => headerMarginBottom ?? '5px'};
  letter-spacing: ${({ letterSpacing }) => (letterSpacing ? `${letterSpacing}px` : '0px')};
  color: ${({ color }) => color || '#33485e'};
  line-height: ${({ headerFontSize }) => {
    const size = headerFontSize ? parseFloat(String(headerFontSize).replace('px', '')) : 15;
    return `${size * 1.25}px`;
  }};
`;

const StyledTooltipContent = styled.div<MultiSelectDropdownTooltipConfig>`
  font-weight: ${({ contentFontWeight }) => contentFontWeight ?? 400};
  font-size: ${({ contentFontSize }) => contentFontSize ?? '14px'};
  letter-spacing: ${({ letterSpacing }) => (letterSpacing ? `${letterSpacing}px` : '0px')};
  color: ${({ color }) => color || '#33485e'};
  line-height: ${({ contentFontSize }) => {
    const size = contentFontSize ? parseFloat(String(contentFontSize).replace('px', '')) : 14;
    return `${size * 1.25}px`;
  }};
`;

type StyledTooltipProps = TooltipProps & {
  $config?: MultiSelectDropdownTooltipConfig;
};

const StyledTooltipBase = React.forwardRef<HTMLDivElement, StyledTooltipProps>((props, ref) => {
  const { $config: _config, className, ...tooltipProps } = props;
  void _config;
  return <Tooltip ref={ref} classes={{ popper: className, tooltip: 'tooltip', arrow: 'arrow' }} {...tooltipProps} />;
});

StyledTooltipBase.displayName = 'StyledTooltipBase';

const StyledTooltip = styled(StyledTooltipBase)`
  & .tooltip {
    color: ${({ $config }) => $config?.color || '#33485e'};
    background-color: ${({ $config }) => $config?.backgroundColor || 'rgba(255, 255, 255, 0.95)'};
    border: ${({ $config }) =>
      $config?.borderColor
        ? `${$config.borderWidth || 1} ${$config.borderStyle || 'solid'} ${$config.borderColor}`
        : 'none'};
    opacity: ${({ $config }) => $config?.opacity ?? 1};
    padding: ${({ $config }) => $config?.padding ?? '10px'};
    box-shadow: 0px 0px 4px rgba(0, 0, 0, 0.15);
    border-radius: ${({ $config }) => $config?.borderRadius ?? '2px'};
    max-width: ${({ $config }) => $config?.maxWidth ?? '320px'};
  }
  & .arrow {
    color: ${({ $config }) => $config?.color || '#33485e'};
    &::before {
      background-color: ${({ $config }) => $config?.backgroundColor || 'rgba(255, 255, 255, 0.95)'};
      border: ${({ $config }) =>
        $config?.borderColor
          ? `${$config.borderWidth || 1} ${$config.borderStyle || 'solid'} ${$config.borderColor}`
          : 'none'};
    }
  }
`;

const StyledAutocompleteOption = styled.div<{ $selected: number }>`
  width: 100%;
  padding: 6px 8px;
  border-radius: 6px;
  background: ${({ $selected }) => ($selected ? '#f0f6ff' : 'transparent')};
  cursor: pointer;
  &:hover {
    background: ${({ $selected }) => ($selected ? '#eaf2ff' : '#f6f2ff')};
  }
  &.with-checkbox {
    display: flex;
    align-items: flex-start;
  }
`;

const OptionLabel = styled.span`
  flex: 1;
  display: block;
  white-space: normal;
  overflow: visible;
  text-overflow: clip;
  word-break: normal;
  line-height: 1.1;
`;

const SelectedValuesSummary = styled.span`
  display: block;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: 15px;
  font-weight: 400;
  color: #33485e;
  padding: 5px 4px !important;
  margin-left: 12px;
`;

const defaultGetOptionValue = (option: MultiSelectDropdownOption) =>
  option?.id ?? option?.value ?? option?.iso ?? option?.name ?? option?.label ?? String(option ?? '');

const defaultGetOptionLabel = (option: MultiSelectDropdownOption) =>
  option?.name ?? option?.label ?? defaultGetOptionValue(option);

const buildTooltip = (tooltip?: MultiSelectDropdownTooltip) => {
  if (!tooltip || (!tooltip.header && !tooltip.content)) return null;
  return (
    <>
      {tooltip.header && <StyledTooltipHeader {...(tooltip.config || {})}>{tooltip.header}</StyledTooltipHeader>}
      {tooltip.content && <StyledTooltipContent {...(tooltip.config || {})}>{tooltip.content}</StyledTooltipContent>}
    </>
  );
};

export function MultiSelectDropdown<TOption extends MultiSelectDropdownOption = MultiSelectDropdownOption>({
  label,
  options,
  value,
  onChange,
  multiple = false,
  disabled,
  placeholder,
  tooltip,
  optionTooltip,
  menuOffset = DEFAULT_MENU_OFFSET,
  getOptionLabel = defaultGetOptionLabel,
  getOptionValue = defaultGetOptionValue,
  summaryFormatter,
  popperModifiers,
  className,
}: MultiSelectDropdownProps<TOption>) {
  const enableMultiSelect = Boolean(multiple);
  const [isOpen, setIsOpen] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const handleOnOpen = useCallback(() => setIsOpen(true), []);
  const handleOnClose = useCallback(() => setIsOpen(false), []);

  const availableOptions = useMemo(() => options || [], [options]);

  const toOption = useCallback(
    (optionValue: string) => {
      if (!optionValue) return null;
      if (!availableOptions.length) {
        return { id: optionValue, name: optionValue } as TOption;
      }
      const matchedOption = availableOptions.find(opt => getOptionValue(opt) === optionValue);
      if (matchedOption) return matchedOption;
      return { id: optionValue, name: optionValue } as TOption;
    },
    [availableOptions, getOptionValue],
  );

  const normalizedMultiValues = useMemo(() => {
    if (!enableMultiSelect) return [] as string[];
    if (Array.isArray(value)) return value;
    if (typeof value === 'string' && value) {
      return value
        .split(',')
        .map(v => v.trim())
        .filter(Boolean);
    }
    return [] as string[];
  }, [enableMultiSelect, value]);

  const selectedOption = useMemo(() => {
    if (enableMultiSelect) return null;
    if (typeof value !== 'string') return null;
    return toOption(value);
  }, [enableMultiSelect, toOption, value]);

  const selectedOptions = useMemo(() => {
    if (!enableMultiSelect) return [] as TOption[];
    return normalizedMultiValues.map(optionValue => toOption(optionValue)).filter(Boolean) as TOption[];
  }, [enableMultiSelect, normalizedMultiValues, toOption]);

  const [localValue, setLocalValue] = useState<TOption[]>(selectedOptions);

  useEffect(() => {
    if (!enableMultiSelect) return;
    setLocalValue(prev => {
      const prevKeys = prev.map(getOptionValue).join('|');
      const nextKeys = selectedOptions.map(getOptionValue).join('|');
      if (prevKeys === nextKeys) return prev;
      return selectedOptions;
    });
  }, [enableMultiSelect, selectedOptions, getOptionValue]);

  const resolvedValue = useMemo(() => {
    if (enableMultiSelect) {
      return localValue;
    }
    if (selectedOption) {
      return selectedOption;
    }
    if (typeof value === 'string' && value) {
      return { id: value, name: value } as TOption;
    }
    return null;
  }, [enableMultiSelect, localValue, selectedOption, value]);

  const multiValueKeys = useMemo(() => {
    if (!enableMultiSelect) return new Set<string>();
    return new Set(localValue.map(getOptionValue));
  }, [enableMultiSelect, getOptionValue, localValue]);

  const defaultSummary = useMemo(() => {
    if (!enableMultiSelect || !localValue.length) return { label: '', title: '' };
    if (localValue.length === 1) {
      const singleLabel = getOptionLabel(localValue[0]);
      return { label: singleLabel, title: singleLabel };
    }
    const labelText = `${localValue.length} items`;
    return {
      label: labelText,
      title: localValue.map(option => getOptionLabel(option)).join(', '),
    };
  }, [enableMultiSelect, localValue, getOptionLabel]);

  const selectedSummary = useMemo(() => {
    if (!enableMultiSelect || !localValue.length) return { label: '', title: '' };
    if (summaryFormatter) {
      const summary = summaryFormatter(localValue);
      return { label: summary.label, title: summary.title ?? summary.label };
    }
    return defaultSummary;
  }, [defaultSummary, enableMultiSelect, localValue, summaryFormatter]);

  useEffect(() => {
    if (!enableMultiSelect) return;
    if (!isOpen) {
      setSearchValue('');
    }
  }, [enableMultiSelect, isOpen]);

  const handleInputChange = useCallback(
    (_event: React.SyntheticEvent<Element, Event>, newInputValue: string, reason: string) => {
      if (!enableMultiSelect) return;
      if (reason === 'reset') {
        setSearchValue('');
        return;
      }
      setSearchValue(newInputValue);
    },
    [enableMultiSelect],
  );

  const containerTooltip = buildTooltip(tooltip);

  return (
    <StyledTooltip
      title={containerTooltip}
      aria-label={tooltip?.content ? String(tooltip.content) : undefined}
      disableFocusListener
      disableHoverListener={!tooltip || (!tooltip.header && !tooltip.content)}
      arrow
      placement="left"
      enterDelay={500}
      $config={tooltip?.config}
      className={className}
    >
      <SelectContainer variant="filled">
        <StyledAutocomplete
          options={availableOptions}
          getOptionLabel={option => getOptionLabel(option as TOption)}
          popupIcon={<ArrowDown />}
          $isopen={isOpen ? 1 : 0}
          disableClearable
          blurOnSelect={!enableMultiSelect}
          disableCloseOnSelect={enableMultiSelect}
          id={`multi-select-${label.replace(/\s+/g, '-').toLowerCase()}`}
          autoComplete
          selectOnFocus={false}
          onOpen={handleOnOpen}
          onClose={(event: any, reason: string) => {
            handleOnClose();
            if (!enableMultiSelect) return;
            if (reason === 'blur') return;
            const formattedValue = localValue.map(option => getOptionValue(option));
            const originalValue = normalizedMultiValues;
            if (formattedValue.join(',') !== originalValue.join(',')) {
              onChange(formattedValue);
            }
          }}
          includeInputInList
          value={resolvedValue as any}
          multiple={enableMultiSelect}
          inputValue={enableMultiSelect ? searchValue : undefined}
          onInputChange={enableMultiSelect ? handleInputChange : undefined}
          renderTags={
            enableMultiSelect
              ? selected => {
                  if (!selected?.length || searchValue) return [];
                  const summary = selectedSummary.label;
                  if (!summary) return [];
                  return [
                    <SelectedValuesSummary key="selected-summary" title={selectedSummary.title || summary}>
                      {summary}
                    </SelectedValuesSummary>,
                  ];
                }
              : undefined
          }
          isOptionEqualToValue={(option: any, optionValue: any) =>
            getOptionValue(option) === getOptionValue(optionValue)
          }
          renderInput={params => (
            <TextField
              label={label}
              {...params}
              margin="normal"
              style={{ margin: '0px', border: 'none' }}
              placeholder={placeholder}
              InputProps={
                {
                  ...params.InputProps,
                  sx: enableMultiSelect
                    ? {
                        paddingLeft: '12px',
                      }
                    : (params.InputProps as unknown as { sx?: unknown })?.sx,
                } as any
              }
            />
          )}
          slotProps={{
            popper: {
              modifiers: [
                {
                  name: 'offset',
                  options: {
                    offset: [menuOffset.horizontal, menuOffset.vertical],
                  },
                },
                ...(popperModifiers || []),
              ],
            },
            paper: {
              sx: {
                backgroundColor: 'var(--background-chart)',
                border: '1px solid var(--filter-bar-dropdown-border-active, #33485e)',
                borderTop: 'none',
                borderRadius: '0 0 5px 5px',
                boxShadow: 'none',
                marginTop: 0,
                '& .MuiAutocomplete-option': {
                  alignItems: 'flex-start',
                  padding: '0 8px !important',
                  minHeight: '0px !important',
                },
              },
            },
          }}
          ListboxProps={{
            style: {
              padding: 0,
            },
          }}
          renderOption={(props, option, state) => {
            const optionTyped = option as TOption;
            const optionSelected = enableMultiSelect
              ? multiValueKeys.has(getOptionValue(optionTyped))
              : Boolean(state?.selected);
            const optionTooltipContent = optionTooltip?.(optionTyped);
            const optionTooltipNode = buildTooltip(optionTooltipContent);

            return (
              <li {...props} key={getOptionValue(optionTyped)}>
                <StyledTooltip
                  title={optionTooltipNode}
                  aria-label={optionTooltipContent?.content ? String(optionTooltipContent.content) : undefined}
                  disableHoverListener={
                    !optionTooltipContent || (!optionTooltipContent.header && !optionTooltipContent.content)
                  }
                  arrow
                  placement="left"
                  enterDelay={500}
                  $config={optionTooltipContent?.config}
                >
                  <StyledAutocompleteOption
                    className={enableMultiSelect ? 'with-checkbox' : undefined}
                    $selected={optionSelected ? 1 : 0}
                  >
                    {enableMultiSelect && (
                      <Checkbox
                        sx={{
                          marginLeft: '-4px',
                          p: 0,
                          pr: '8px',
                          alignSelf: 'flex-start',
                          mt: 0,
                        }}
                        checked={multiValueKeys.has(getOptionValue(optionTyped))}
                        tabIndex={-1}
                        disableRipple
                        size="small"
                      />
                    )}
                    <OptionLabel>{getOptionLabel(optionTyped)}</OptionLabel>
                  </StyledAutocompleteOption>
                </StyledTooltip>
              </li>
            );
          }}
          onChange={(_event, newValue) => {
            if (enableMultiSelect) {
              if (!Array.isArray(newValue)) {
                setLocalValue([]);
                return;
              }
              setLocalValue(newValue as TOption[]);
              return;
            }
            if (!newValue) {
              onChange('');
              return;
            }
            const nextValue = getOptionValue(newValue as TOption);
            onChange(nextValue);
          }}
          disabled={disabled}
        />
      </SelectContainer>
    </StyledTooltip>
  );
}

export default MultiSelectDropdown;
